import { useCallback, useRef } from 'react';
import { useCasePartner } from '@/components/contexts/CasePartnerContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import type { ClientUiContext, SendMessageStartResult } from '@/hooks/useChat';
import { sanitizeUserInput } from '@/src/utils/sanitization';

interface SendToCasePartnerPanelOptions {
  uiContext?: ClientUiContext;
  webSearch?: boolean;
}

export interface SendToCasePartnerPanelResult {
  sent: boolean;
  conflict?: boolean;
  error?: string;
}

export const START_FAILED_MESSAGE = 'Could not start Case Partner. Please try again.';

/**
 * Start a new Case Partner chat with the given message, then open it in the
 * side panel. The page composer stays unattached so the next send can start
 * another chat in parallel.
 */
export function useSendToCasePartnerPanel(matterGuid: string) {
  const casePartner = useCasePartner();
  const { showCasePartnerTab } = useSidePanel();
  const handoffGenerationRef = useRef(0);

  return useCallback(async (
    rawInput: string,
    options: SendToCasePartnerPanelOptions = {},
  ): Promise<SendToCasePartnerPanelResult> => {
    const sanitizedInput = sanitizeUserInput(rawInput);
    if (!sanitizedInput.trim()) {
      return { sent: false };
    }

    const handoffGeneration = ++handoffGenerationRef.current;

    if (matterGuid) {
      casePartner.activate(matterGuid);
    }

    casePartner.setUserInput('');
    casePartner.clearSelections();
    casePartner.setShowAllDocuments(false);
    casePartner.setRouterQueryDocuments(undefined);
    casePartner.setIsComposingNewChat(false);

    try {
      const start = await new Promise<SendMessageStartResult>((resolve) => {
        // Await start/claim only — the stream can keep running while this
        // composer starts another chat. sendMessage detaches the previous SSE
        // client when forceNewChat is set; the server turn continues.
        void casePartner.sendMessage(
          sanitizedInput,
          [],
          {
            forceNewChat: true,
            onStartSettled: resolve,
            webSearch: Boolean(options.webSearch),
            ...(options.uiContext ? { uiContext: options.uiContext } : {}),
          },
        );
      });

      if (start.conflict) {
        return { sent: false, conflict: true };
      }
      if (!start.started) {
        return { sent: false, error: start.error || START_FAILED_MESSAGE };
      }
      // A later handoff can settle first; only the latest start owns the shared toggle.
      if (handoffGeneration === handoffGenerationRef.current && 'webSearch' in options) {
        casePartner.setWebSearchEnabled(Boolean(options.webSearch));
      }
      showCasePartnerTab({ activate: true, open: true });
      return { sent: true };
    } catch (error: any) {
      return {
        sent: false,
        error: error?.response?.data?.detail || error?.message || START_FAILED_MESSAGE,
      };
    }
  }, [
    matterGuid,
    casePartner.activate,
    casePartner.sendMessage,
    casePartner.setUserInput,
    casePartner.clearSelections,
    casePartner.setShowAllDocuments,
    casePartner.setRouterQueryDocuments,
    casePartner.setIsComposingNewChat,
    casePartner.setWebSearchEnabled,
    showCasePartnerTab,
  ]);
}
